import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import BrowserFrame from "./BrowserFrame";
import { RevealWords } from "./Motion";

const PROJECTS = [
  {
    name: "MKS Solar",
    url: "mkssolar.de",
    href: "https://mkssolar.de",
    category: "Photovoltaik",
    tags: ["Webdesign", "Entwicklung", "PV-Rechner"],
    result: "+180% Anfragen über die Website",
    src: "/refs/iym24de.png",
  },
  {
    name: "ViralUp",
    url: "viralup.eu",
    href: "https://viralup.eu",
    category: "Social Media Agentur",
    tags: ["Webdesign", "Branding", "Motion"],
    result: "Neuer Markenauftritt mit Wow-Effekt",
    src: "/refs/viralup.png",
  },
  {
    name: "Der Steuerberater",
    url: "dersteuerberater.de",
    href: "https://dersteuerberater.de",
    category: "Steuerberatung",
    tags: ["Webdesign", "Google Ads", "SEO"],
    result: "94% Weiterempfehlungsquote",
    src: "/refs/steuerberater.png",
  },
  {
    name: "Clean & Glanz",
    url: "cleanglanzreinigungservice.de",
    href: "https://cleanglanzreinigungservice.de/",
    category: "Reinigungsservice",
    tags: ["Webdesign", "Local SEO", "Content"],
    result: "Premium-Auftritt für lokale Kunden",
    src: "/refs/iym24com.png",
  },
  {
    name: "Tennisschule Baseline",
    url: "tennisschule-baseline.de",
    href: "https://tennisschule-baseline.de/",
    category: "Tennisschule Dresden",
    tags: ["Webdesign", "Video-Hero", "Content"],
    result: "Kinetischer Auftritt mit Vollbild-Video",
    src: "/refs/tennisschule.png",
  },
];

function WorkItem({ p, i }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [60, -60]);
  const left = i % 2 === 0;

  return (
    <div
      ref={ref}
      data-testid={`work-${i}`}
      className={`grid grid-cols-1 items-center gap-8 lg:grid-cols-12 lg:gap-14 ${!left ? "lg:[&>*:first-child]:order-2" : ""}`}
    >
      {/* Screenshot flying in */}
      <motion.div
        style={{ y }}
        initial={{ opacity: 0, x: left ? -90 : 90, rotate: left ? -5 : 5 }}
        whileInView={{ opacity: 1, x: 0, rotate: left ? -2 : 2 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
        className="lg:col-span-7"
      >
        <a
          href={p.href}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`work-link-${i}`}
          className="group/link relative block cursor-pointer"
        >
          <motion.div whileHover={{ rotate: 0, scale: 1.02 }} transition={{ duration: 0.5 }}>
            <BrowserFrame src={p.src} url={p.url} alt={`${p.name} Website – ${p.category} – Referenz von IYM Webdesign`} imgClassName="h-[280px] md:h-[400px]" />
          </motion.div>
          <span className="pointer-events-none absolute bottom-5 left-1/2 flex -translate-x-1/2 items-center gap-2 rounded-full bg-ink px-5 py-2.5 font-display text-xs uppercase tracking-wide text-white opacity-0 shadow-xl transition-all duration-300 group-hover/link:-translate-y-1 group-hover/link:opacity-100">
            Website besuchen
            <ArrowUpRight className="h-4 w-4" />
          </span>
        </a>
      </motion.div>

      {/* Info */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.8, delay: 0.15 }}
        className="lg:col-span-5"
      >
        <span className="font-mono text-xs uppercase tracking-[0.15em] text-[#2c3bff]">
          {String(i + 1).padStart(2, "0")} · {p.category}
        </span>
        <h3 className="mt-3 font-display text-4xl text-ink md:text-5xl">{p.name}</h3>
        <div className="mt-4 flex flex-wrap gap-2">
          {p.tags.map((t) => (
            <span
              key={t}
              className="rounded-full border border-black/10 bg-white px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-[#4a453d]"
            >
              {t}
            </span>
          ))}
        </div>
        <div className="mt-6 flex items-center gap-3 rounded-xl border border-[#2c3bff]/20 bg-[#2c3bff]/5 px-4 py-3">
          <ArrowUpRight className="h-5 w-5 shrink-0 text-[#2c3bff]" />
          <span className="font-display text-lg text-ink">{p.result}</span>
        </div>
      </motion.div>
    </div>
  );
}

export default function Work() {
  return (
    <section id="arbeiten" className="bg-paper-deep py-24 md:py-36">
      <div className="mx-auto max-w-[1360px] px-5 md:px-10">
        <div className="mb-16 flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <div>
            <p className="mb-4 font-mono text-xs uppercase tracking-[0.2em] text-[#2c3bff]">/ Ausgewählte Arbeiten</p>
            <h2 className="max-w-2xl font-display text-4xl leading-[0.95] text-ink sm:text-5xl md:text-6xl">
              <RevealWords text="Projekte, die für sich sprechen." />
            </h2>
          </div>
          <p className="max-w-xs text-[#565149]">
            Echte Kundenseiten, gebaut von IYM. Live und im Einsatz.
          </p>
        </div>

        <div className="flex flex-col gap-24 md:gap-32">
          {PROJECTS.map((p, i) => (
            <WorkItem key={p.name} p={p} i={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
